
import React from 'react';
import { GraphTeam } from '@/hooks/useGraphTeams';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import '@/styles/apple-style.css';

// Import our new components
import TeamCardHeader from './team-card/TeamCardHeader';
import ComplianceScoreDisplay from './team-card/ComplianceScoreDisplay';
import TeamBasicStats from './team-card/TeamBasicStats';
import GovernanceKPISection from './team-card/GovernanceKPISection';
import GovernanceStatus from './team-card/GovernanceStatus';
import CardFooter from './team-card/CardFooter';

// Import utility functions
import { 
  getComplianceStatus, 
  getComplianceColor, 
  getRiskLevel,
  getRecommendedPolicy
} from './team-card/utils/complianceUtils';

interface GraphTeamCardProps {
  team: GraphTeam;
}

const GraphTeamCard = ({ team }: GraphTeamCardProps) => {
  const navigate = useNavigate();
  
  // Handle policy executor navigation with specific policy
  const handlePolicyExecute = (e: React.MouseEvent, policyId?: number) => {
    e.stopPropagation();
    const policyParam = policyId ? `&id=${policyId}` : '';
    navigate(`/policy-auto-executor?teamId=${team.id}&teamName=${encodeURIComponent(team.displayName)}${policyParam}`);
  };
  
  const complianceScore = team.compliance || 0;
  const complianceStatus = getComplianceStatus(complianceScore);
  const complianceColor = getComplianceColor(complianceScore);
  const riskLevel = getRiskLevel(complianceScore);

  // Mock data for governance KPIs
  const governanceKPIs = {
    inactivityStats: Math.floor(Math.random() * 31) + 5, // 5-35 days
    inactivityThreshold: 30, // days
    averageInactivePeriod: Math.floor(Math.random() * 15) + 2, // 2-16 days
  };
  
  const recommendedPolicy = getRecommendedPolicy(
    governanceKPIs.inactivityStats, 
    governanceKPIs.inactivityThreshold
  );
  
  const isInactivityCritical = governanceKPIs.inactivityStats > governanceKPIs.inactivityThreshold;

  return (
    <Card 
      className="flex flex-col h-full dark:bg-gray-800/95 border-0 shadow-md p-6 pt-4 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
      onClick={() => navigate(`/teams/${team.id}`)}
    >
      <TeamCardHeader 
        displayName={team.displayName}
        description={team.description}
      />
      
      <ComplianceScoreDisplay 
        complianceScore={complianceScore}
        complianceColor={complianceColor}
        complianceStatus={complianceStatus}
      />
      
      <TeamBasicStats 
        members={team.members || 0}
        lastActivity={team.lastActivity || 'N/A'}
      />
      
      <GovernanceKPISection 
        inactivityStats={governanceKPIs.inactivityStats}
        inactivityThreshold={governanceKPIs.inactivityThreshold}
        averageInactivePeriod={governanceKPIs.averageInactivePeriod}
        recommendedPolicy={recommendedPolicy}
        isInactivityCritical={isInactivityCritical}
        onPolicyExecute={handlePolicyExecute}
      />
      
      <GovernanceStatus />
      
      <CardFooter 
        riskLevel={riskLevel}
        onPolicyExecute={handlePolicyExecute}
      />
    </Card>
  );
};

export default GraphTeamCard;
